import { useState, useMemo } from "react";
import IntranetLayout from "@/components/IntranetLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Shield, AlertTriangle, CheckCircle, Clock, Mail, Download, Printer, Plus, Eye, Edit2, Upload, MoreHorizontal, User } from "lucide-react";
import {
  Certificate, CrewMember, EscalationEntry, CertStatus,
  getCertStatus, generateFleetCertificates, generateCrewMembers, COMPANY_DOCUMENTS, generateEscalationLog
} from "./watchdog/watchdogData";
import { cn } from "@/lib/utils";

const STATUS_STYLES: Record<CertStatus, { bg: string; text: string; label: string }> = {
  EXPIRED: { bg: "bg-[#C83228]", text: "text-white", label: "EXPIRED" },
  CRITICAL: { bg: "bg-[#C83228]", text: "text-white", label: "CRITICAL" },
  WARNING: { bg: "bg-[#E8A020]", text: "text-white", label: "WARNING" },
  VALID: { bg: "bg-[#2CA048]", text: "text-white", label: "VALID" },
};

const StatusBadge = ({ status, pulse }: { status: CertStatus; pulse?: boolean }) => {
  const s = STATUS_STYLES[status];
  return (
    <span className={cn("inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold", s.bg, s.text, pulse && status === "CRITICAL" && "animate-pulse")}>
      {s.label}
    </span>
  );
};

export const ComplianceWatchdogContent = () => {
  const [fleetCerts, setFleetCerts] = useState(() => generateFleetCertificates());
  const [crewMembers] = useState(() => generateCrewMembers());
  const [companyDocs] = useState(COMPANY_DOCUMENTS);
  const [escalationLog] = useState(() => {
    const fc = generateFleetCertificates();
    const cm = generateCrewMembers();
    return generateEscalationLog(fc, cm);
  });
  const [search, setSearch] = useState("");
  const [vesselFilter, setVesselFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [selectedCrew, setSelectedCrew] = useState<CrewMember | null>(null);
  const [addCertOpen, setAddCertOpen] = useState(false);

  // Summary counts
  const allCerts = useMemo(() => {
    const crew = crewMembers.flatMap(cm => cm.certificates);
    return [...fleetCerts, ...crew, ...companyDocs];
  }, [fleetCerts, crewMembers, companyDocs]);

  const summary = useMemo(() => {
    let expired = 0, critical = 0, warning = 0, valid = 0;
    allCerts.forEach(c => {
      const { status } = getCertStatus(c.expiryDate);
      if (status === "EXPIRED") expired++;
      else if (status === "CRITICAL") critical++;
      else if (status === "WARNING") warning++;
      else valid++;
    });
    return { expired, critical, warning, valid, total: allCerts.length };
  }, [allCerts]);

  // Risk banner
  const riskBanner = useMemo(() => {
    if (summary.expired > 0) return { type: "error" as const, msg: `⚠️ OPERATIONAL RISK: ${summary.expired} expired certificate(s) require immediate action` };
    if (summary.critical > 0) return { type: "warning" as const, msg: `CRITICAL: ${summary.critical} certificate(s) expire within 30 days` };
    // Find nearest expiry
    const validCerts = allCerts.filter(c => getCertStatus(c.expiryDate).status === "VALID");
    if (validCerts.length > 0) {
      const nearest = validCerts.reduce((a, b) => getCertStatus(a.expiryDate).daysRemaining < getCertStatus(b.expiryDate).daysRemaining ? a : b);
      const { daysRemaining } = getCertStatus(nearest.expiryDate);
      return { type: "success" as const, msg: `All certificates valid — next expiry in ${daysRemaining} days (${nearest.name})` };
    }
    return { type: "success" as const, msg: "All certificates valid" };
  }, [summary, allCerts]);

  // Filtered fleet certs
  const filteredFleet = useMemo(() => {
    return fleetCerts.filter(c => {
      if (vesselFilter !== "all" && c.vessel !== vesselFilter) return false;
      if (statusFilter !== "all" && getCertStatus(c.expiryDate).status !== statusFilter) return false;
      if (categoryFilter !== "all" && c.category !== categoryFilter) return false;
      if (search && !c.name.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [fleetCerts, vesselFilter, statusFilter, categoryFilter, search]);

  const handleSendReminder = (cert: Certificate) => {
    toast.success(`Escalation email sent for ${cert.name}`, { description: `Reminder sent to ${cert.owner}` });
  };

  const exportCSV = () => {
    const rows = allCerts.map(c => {
      const { status, daysRemaining } = getCertStatus(c.expiryDate);
      return [c.vessel || c.crewMember || "Company", c.name, c.issuingAuthority, c.issueDate, c.expiryDate, status, daysRemaining, c.owner].join(",");
    });
    const csv = "Vessel/Owner,Certificate,Authority,Issue Date,Expiry,Status,Days Remaining,Owner\n" + rows.join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "VOFF_Certificate_Register.csv"; a.click();
    toast.success("Certificate register exported");
  };

  const categories = [...new Set(fleetCerts.map(c => c.category))];

  return (
    <>
      {/* Page Header */}
      <div className="bg-[#00283C] px-4 md:px-6 py-5 mb-4 rounded-lg">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h1 className="text-[#288CC8] font-display text-xl md:text-2xl tracking-wider uppercase font-bold flex items-center gap-2">
              <Shield className="w-6 h-6" /> COMPLIANCE WATCHDOG
            </h1>
            <p className="text-[#6B8A9A] text-sm mt-0.5 font-body">Certificate Lifecycle Management & Automated Escalation</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className="bg-[#C83228]/20 border-[#C83228] text-[#C83228] font-bold text-xs">🔴 {summary.expired + summary.critical} CRITICAL</Badge>
            <Badge variant="outline" className="bg-[#E8A020]/20 border-[#E8A020] text-[#E8A020] font-bold text-xs">🟠 {summary.warning} WARNING</Badge>
            <Badge variant="outline" className="bg-[#2CA048]/20 border-[#2CA048] text-[#2CA048] font-bold text-xs">🟢 {summary.valid} VALID</Badge>
            <Badge variant="outline" className="bg-white/10 border-white/30 text-white font-bold text-xs">⬜ {summary.total} TOTAL</Badge>
          </div>
        </div>
      </div>

      {/* Risk Banner */}
      <div className={cn(
        "rounded-lg px-4 py-3 mb-4 font-body text-sm font-semibold",
        riskBanner.type === "error" && "bg-[#C83228]/15 text-[#C83228] border border-[#C83228]/30",
        riskBanner.type === "warning" && "bg-[#E8A020]/15 text-[#E8A020] border border-[#E8A020]/30",
        riskBanner.type === "success" && "bg-[#2CA048]/15 text-[#2CA048] border border-[#2CA048]/30",
      )}>
        {riskBanner.msg}
      </div>

      {/* Action Buttons */}
      <div className="flex items-center gap-2 mb-4">
        <Button size="sm" variant="outline" onClick={exportCSV}><Download className="w-4 h-4 mr-1" /> Export CSV</Button>
        <Button size="sm" variant="outline" onClick={() => window.print()}><Printer className="w-4 h-4 mr-1" /> Print</Button>
      </div>

      <Tabs defaultValue="fleet">
        <TabsList className="mb-4">
          <TabsTrigger value="fleet">Fleet Certificates</TabsTrigger>
          <TabsTrigger value="crew">Crew Certifications</TabsTrigger>
          <TabsTrigger value="company">Company Documents</TabsTrigger>
          <TabsTrigger value="escalation">Escalation Log</TabsTrigger>
        </TabsList>

        {/* TAB 1: Fleet Certificates */}
        <TabsContent value="fleet">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <Select value={vesselFilter} onValueChange={setVesselFilter}>
              <SelectTrigger className="w-[200px] h-8 text-xs"><SelectValue placeholder="All Vessels" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Vessels</SelectItem>
                <SelectItem value="VOFF Crewliner 01">VOFF Crewliner 01</SelectItem>
                <SelectItem value="VOFF Crewliner 02">VOFF Crewliner 02</SelectItem>
                <SelectItem value="VOFF Combi 01">VOFF Combi 01</SelectItem>
              </SelectContent>
            </Select>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-[160px] h-8 text-xs"><SelectValue placeholder="Category" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[140px] h-8 text-xs"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="EXPIRED">Expired</SelectItem>
                <SelectItem value="CRITICAL">Critical</SelectItem>
                <SelectItem value="WARNING">Warning</SelectItem>
                <SelectItem value="VALID">Valid</SelectItem>
              </SelectContent>
            </Select>
            <Input placeholder="Search certificate..." value={search} onChange={e => setSearch(e.target.value)} className="h-8 text-xs w-[200px]" />
            <Button size="sm" className="ml-auto bg-[#288CC8] hover:bg-[#14508C] h-8 text-xs" onClick={() => setAddCertOpen(true)}>
              <Plus className="w-3 h-3 mr-1" /> Add Certificate
            </Button>
          </div>

          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#00283C]/5">
                    <TableHead className="text-xs">Vessel</TableHead>
                    <TableHead className="text-xs">Certificate</TableHead>
                    <TableHead className="text-xs">Authority</TableHead>
                    <TableHead className="text-xs">Issue Date</TableHead>
                    <TableHead className="text-xs">Expiry</TableHead>
                    <TableHead className="text-xs">Status</TableHead>
                    <TableHead className="text-xs text-right">Days</TableHead>
                    <TableHead className="text-xs">Owner</TableHead>
                    <TableHead className="text-xs">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredFleet.map(cert => {
                    const { status, daysRemaining } = getCertStatus(cert.expiryDate);
                    return (
                      <TableRow key={cert.id} className={cn(status === "EXPIRED" && "bg-[#C83228]/5", status === "CRITICAL" && "bg-[#C83228]/5")}>
                        <TableCell className="text-xs font-medium">{cert.vessel}</TableCell>
                        <TableCell className="text-xs">{cert.name}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{cert.issuingAuthority}</TableCell>
                        <TableCell className="text-xs">{cert.issueDate}</TableCell>
                        <TableCell className="text-xs">{cert.expiryDate}</TableCell>
                        <TableCell><StatusBadge status={status} pulse={status === "CRITICAL"} /></TableCell>
                        <TableCell className={cn("text-xs text-right font-bold", daysRemaining <= 0 ? "text-[#C83228]" : daysRemaining <= 30 ? "text-[#C83228]" : daysRemaining <= 90 ? "text-[#E8A020]" : "text-[#2CA048]")}>{daysRemaining}</TableCell>
                        <TableCell className="text-xs">{cert.owner}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleSendReminder(cert)} title="Send Reminder"><Mail className="w-3 h-3" /></Button>
                            <Button onClick={() => toast.success("Action recorded: Action")} variant="ghost" size="icon" className="h-6 w-6" title="Edit"><Edit2 className="w-3 h-3" /></Button>
                            <Button onClick={() => toast.success("Action recorded: Action")} variant="ghost" size="icon" className="h-6 w-6" title="Upload"><Upload className="w-3 h-3" /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB 2: Crew Certifications */}
        <TabsContent value="crew">
          <div className="flex gap-4">
            {/* Left Sidebar */}
            <div className="w-[240px] shrink-0 space-y-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase mb-2">Crew Members</p>
              {crewMembers.map(cm => {
                const statuses = cm.certificates.map(c => getCertStatus(c.expiryDate).status);
                const hasExpired = statuses.includes("EXPIRED");
                const hasCritical = statuses.includes("CRITICAL");
                const hasWarning = statuses.includes("WARNING");
                return (
                  <button
                    key={cm.id}
                    onClick={() => setSelectedCrew(cm)}
                    className={cn(
                      "w-full text-left rounded-lg border p-2.5 transition-colors hover:bg-accent",
                      selectedCrew?.id === cm.id && "border-[#288CC8] bg-[#288CC8]/10"
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-[#00283C] text-white flex items-center justify-center text-xs font-bold">
                        {cm.name.split(" ").map(n => n[0]).join("").slice(0, 2)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold truncate">{cm.name}</p>
                        <p className="text-[10px] text-muted-foreground">{cm.role} • {cm.vessel.replace("VOFF ", "")}</p>
                      </div>
                    </div>
                    <div className="flex gap-1 mt-1.5">
                      {hasExpired && <span className="w-2 h-2 rounded-full bg-[#C83228]" />}
                      {hasCritical && <span className="w-2 h-2 rounded-full bg-[#C83228] animate-pulse" />}
                      {hasWarning && <span className="w-2 h-2 rounded-full bg-[#E8A020]" />}
                      {!hasExpired && !hasCritical && !hasWarning && <span className="w-2 h-2 rounded-full bg-[#2CA048]" />}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Right Panel */}
            <div className="flex-1 min-w-0">
              {selectedCrew ? (
                <>
                  <Card className="mb-3">
                    <CardContent className="p-4 flex items-center gap-4">
                      <div className="w-14 h-14 rounded-full bg-[#00283C] text-white flex items-center justify-center text-lg font-bold">
                        {selectedCrew.name.split(" ").map(n => n[0]).join("").slice(0, 2)}
                      </div>
                      <div>
                        <h3 className="font-display text-base font-bold">{selectedCrew.name}</h3>
                        <p className="text-xs text-muted-foreground">{selectedCrew.role} • {selectedCrew.vessel} • {selectedCrew.nationality}</p>
                        <div className="flex gap-2 mt-1">
                          {(() => {
                            const s = selectedCrew.certificates.reduce((acc, c) => { const st = getCertStatus(c.expiryDate).status; acc[st] = (acc[st] || 0) + 1; return acc; }, {} as Record<string, number>);
                            return Object.entries(s).map(([k, v]) => (
                              <span key={k} className="text-[10px] font-semibold">{v} {k.toLowerCase()}</span>
                            ));
                          })()}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-0 overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-[#00283C]/5">
                            <TableHead className="text-xs">Certificate</TableHead>
                            <TableHead className="text-xs">Issuing Body</TableHead>
                            <TableHead className="text-xs">Issue Date</TableHead>
                            <TableHead className="text-xs">Expiry</TableHead>
                            <TableHead className="text-xs">Status</TableHead>
                            <TableHead className="text-xs text-right">Days</TableHead>
                            <TableHead className="text-xs">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {selectedCrew.certificates.map(cert => {
                            const { status, daysRemaining } = getCertStatus(cert.expiryDate);
                            return (
                              <TableRow key={cert.id}>
                                <TableCell className="text-xs font-medium">{cert.name}</TableCell>
                                <TableCell className="text-xs text-muted-foreground">{cert.issuingAuthority}</TableCell>
                                <TableCell className="text-xs">{cert.issueDate}</TableCell>
                                <TableCell className="text-xs">{cert.expiryDate}</TableCell>
                                <TableCell><StatusBadge status={status} pulse={status === "CRITICAL"} /></TableCell>
                                <TableCell className={cn("text-xs text-right font-bold", daysRemaining <= 0 ? "text-[#C83228]" : daysRemaining <= 90 ? "text-[#E8A020]" : "text-[#2CA048]")}>{daysRemaining}</TableCell>
                                <TableCell>
                                  <div className="flex gap-1">
                                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleSendReminder(cert)}><Mail className="w-3 h-3" /></Button>
                                    <Button onClick={() => toast.success("Action recorded: Action")} variant="ghost" size="icon" className="h-6 w-6"><Edit2 className="w-3 h-3" /></Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </>
              ) : (
                <div className="flex items-center justify-center h-64 text-muted-foreground text-sm">
                  <User className="w-5 h-5 mr-2" /> Select a crew member to view their certifications
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* TAB 3: Company Documents */}
        <TabsContent value="company">
          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#00283C]/5">
                    <TableHead className="text-xs">Document</TableHead>
                    <TableHead className="text-xs">Ref</TableHead>
                    <TableHead className="text-xs">Authority</TableHead>
                    <TableHead className="text-xs">Owner</TableHead>
                    <TableHead className="text-xs">Issue Date</TableHead>
                    <TableHead className="text-xs">Next Review / Expiry</TableHead>
                    <TableHead className="text-xs">Status</TableHead>
                    <TableHead className="text-xs text-right">Days</TableHead>
                    <TableHead className="text-xs">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {companyDocs.map(doc => {
                    const { status, daysRemaining } = getCertStatus(doc.expiryDate);
                    return (
                      <TableRow key={doc.id} className={cn(status === "EXPIRED" && "bg-[#C83228]/5")}>
                        <TableCell className="text-xs font-medium">{doc.name}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{doc.documentRef}</TableCell>
                        <TableCell className="text-xs">{doc.issuingAuthority}</TableCell>
                        <TableCell className="text-xs">{doc.owner}</TableCell>
                        <TableCell className="text-xs">{doc.issueDate}</TableCell>
                        <TableCell className="text-xs">{doc.expiryDate}</TableCell>
                        <TableCell><StatusBadge status={status} /></TableCell>
                        <TableCell className={cn("text-xs text-right font-bold", daysRemaining <= 0 ? "text-[#C83228]" : daysRemaining <= 90 ? "text-[#E8A020]" : "text-[#2CA048]")}>{daysRemaining}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleSendReminder(doc)}><Mail className="w-3 h-3" /></Button>
                            <Button onClick={() => toast.success("Action recorded: Action")} variant="ghost" size="icon" className="h-6 w-6"><Edit2 className="w-3 h-3" /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB 4: Escalation Log */}
        <TabsContent value="escalation">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            {[
              { label: "Reminders This Month", value: escalationLog.length.toString() },
              { label: "Open Rate", value: `${Math.round(escalationLog.filter(e => e.deliveryStatus === "opened").length / Math.max(1, escalationLog.length) * 100)}%` },
              { label: "Renewed After Reminder", value: "67%" },
              { label: "Escalations to COO", value: escalationLog.filter(e => e.type === "AUTO-30" || e.type === "AUTO-EXPIRED").length.toString() },
            ].map(s => (
              <Card key={s.label}><CardContent className="p-3 text-center"><p className="text-lg font-display font-bold">{s.value}</p><p className="text-[10px] text-muted-foreground">{s.label}</p></CardContent></Card>
            ))}
          </div>
          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#00283C]/5">
                    <TableHead className="text-xs">Timestamp</TableHead>
                    <TableHead className="text-xs">Recipient</TableHead>
                    <TableHead className="text-xs">Certificate</TableHead>
                    <TableHead className="text-xs text-right">Days</TableHead>
                    <TableHead className="text-xs">Type</TableHead>
                    <TableHead className="text-xs">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {escalationLog.map(entry => (
                    <TableRow key={entry.id}>
                      <TableCell className="text-xs">{new Date(entry.timestamp).toLocaleDateString()}</TableCell>
                      <TableCell className="text-xs font-medium">{entry.recipient}</TableCell>
                      <TableCell className="text-xs">{entry.certificate}</TableCell>
                      <TableCell className="text-xs text-right">{entry.daysAtSend}</TableCell>
                      <TableCell><Badge variant="outline" className="text-[10px]">{entry.type}</Badge></TableCell>
                      <TableCell>
                        <span className={cn("text-xs font-semibold", entry.deliveryStatus === "sent" && "text-[#288CC8]", entry.deliveryStatus === "opened" && "text-[#2CA048]", entry.deliveryStatus === "failed" && "text-[#C83228]")}>
                          {entry.deliveryStatus === "sent" ? "✓ Sent" : entry.deliveryStatus === "opened" ? "👁 Opened" : "✗ Failed"}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Certificate Dialog */}
      <Dialog open={addCertOpen} onOpenChange={setAddCertOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Certificate</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Vessel</Label>
              <Select><SelectTrigger><SelectValue placeholder="Select vessel" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="VOFF Crewliner 01">VOFF Crewliner 01</SelectItem>
                  <SelectItem value="VOFF Crewliner 02">VOFF Crewliner 02</SelectItem>
                  <SelectItem value="VOFF Combi 01">VOFF Combi 01</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Certificate Name</Label><Input className="h-8 text-xs" /></div>
            <div><Label className="text-xs">Issuing Authority</Label><Input className="h-8 text-xs" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Issue Date</Label><Input type="date" className="h-8 text-xs" /></div>
              <div><Label className="text-xs">Expiry Date</Label><Input type="date" className="h-8 text-xs" /></div>
            </div>
            <div><Label className="text-xs">Category</Label>
              <Select><SelectTrigger><SelectValue placeholder="Category" /></SelectTrigger>
                <SelectContent>
                  {["Class", "Flag State", "Safety Equipment", "Navigation", "Insurance", "Environment", "OVMSA"].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Owner / Responsible Party</Label><Input className="h-8 text-xs" /></div>
            <Button className="w-full bg-[#288CC8] hover:bg-[#14508C]" onClick={() => { setAddCertOpen(false); toast.success("Certificate added"); }}>Save Certificate</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

const ComplianceWatchdog = () => (
  <IntranetLayout>
    <ComplianceWatchdogContent />
  </IntranetLayout>
);

export default ComplianceWatchdog;
